import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, ListItem, IconButton, ListItemText, Stack, Tooltip, Badge, Button } from '@mui/material';
import classnames from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { ChatIcon, ChromeIcon, ErrorOutlineIcon, FirefoxIcon, SafariIcon, VideoCameraIcon } from 'assets';
import { ApiUser, HubPatient } from 'types';
import { ModalTitle, PatientStatus } from 'components';
import { useChat } from 'hooks';
import { checkBrowserVersion, checkOsVersion } from 'utils';
import { DotMenu } from '../DotMenu';
import { PatientListDialogType } from '../../PatientList';

import styles from './PatientListItem.module.scss';

type PatientListItemProps = {
  patient: HubPatient;
  isCallStarted?: boolean;
  isListPinned?: boolean;
  onStartCall: (id: string) => void;
  onAfterStartCall?: () => void;
  onAfterOpenChat?: () => void;
  onDialogOpen: (type: PatientListDialogType) => void;
  loadingPatientId?: string | null;
  setLoadingPatientId?: (id: string | null) => void;
};

export function PatientListItem({
  patient,
  isCallStarted = false,
  isListPinned = false,
  onStartCall,
  onAfterStartCall,
  onAfterOpenChat,
  onDialogOpen,
  loadingPatientId,
  setLoadingPatientId,
}: PatientListItemProps) {
  const { t, i18n } = useTranslation('providerRoom');
  const isRtl = i18n.dir() === 'rtl';
  const { identity, conversationsUnreadMessagesData, openChat } = useChat();
  const user: ApiUser | null = useAppSelector(selectUser);

  const [waitingTime, setWaitingTime] = useState(0);

  const browserCheck = useMemo(() => checkBrowserVersion(patient.device?.browser), [patient.device]);
  const osCheck = useMemo(() => checkOsVersion(patient.device?.operationSystem), [patient.device]);

  const hasBadState = useMemo(() => {
    const accessBad = !(patient.callAccesses?.hasMicroAccess && patient.callAccesses?.hasCameraAccess);
    return accessBad || !browserCheck.ok || !osCheck.ok;
  }, [patient, browserCheck, osCheck]);

  const unreadMessagesCount: number = useMemo(() => {
    return conversationsUnreadMessagesData.get(`$chat-${identity}-${patient.connectionId}`)?.unreadMessagesCount ?? 0;
  }, [conversationsUnreadMessagesData, identity, patient]);

  const handleOpenChat = useCallback((): void => {
    openChat(identity, patient.connectionId);
    onAfterOpenChat?.();
  }, [identity, onAfterOpenChat, openChat, patient.connectionId]);

  const handleStartCall = (id: string) => {
    setLoadingPatientId?.(id); // ✅ trigger global loading spinner
    onStartCall(id);
    onAfterStartCall?.();
  };

  const waitingColor = useMemo(() => {
    if (waitingTime < 5) return '#3eb157';
    return '#ff3c3c';
  }, [waitingTime]);

  const browserIconSmall = useMemo(() => {
    const browser = patient.device?.browser?.toLowerCase();
    if (!browser) return null;
    const sx = { fontSize: '1.35rem', display: 'block' };
    if (browser.includes('firefox') || browser.includes('mozilla')) return <FirefoxIcon sx={sx} />;
    if (browser.includes('safari')) return <SafariIcon sx={sx} />;
    return <ChromeIcon sx={sx} />;
  }, [patient.device]);

  useEffect(() => {
    const updateWaitingTime = () => {
      const now = moment();
      const joined = moment(patient.joinedOn);
      const minutes = Math.max(0, Math.floor(moment.duration(now.diff(joined)).asMinutes()));
      setWaitingTime(minutes);
    };

    updateWaitingTime();
    const timer = setInterval(updateWaitingTime, 1000);

    return () => clearInterval(timer);
  }, [patient.joinedOn]);

  const isActionsDisabled = !!loadingPatientId;

  return (
    <ListItem
      key={patient.connectionId}
      secondaryAction={
        <Box
          className={classnames(styles.actions, { [styles.actionsRtl]: isRtl, [styles.actionsCompact]: isCallStarted })}
        >
          {isCallStarted ? (
            <Button
              size='small'
              variant='contained'
              onClick={() => handleStartCall(patient.connectionId)}
              disabled={isActionsDisabled}
              className={styles.addToCallButton}
            >
              {t('join')}
            </Button>
          ) : (
            <IconButton
              edge='end'
              aria-label={t('aria.video')}
              onClick={() => handleStartCall(patient.connectionId)}
              disabled={isActionsDisabled}
            >
              <div className={styles.iconWrapper}>
                <VideoCameraIcon className={styles.videoCameraIcon} />
              </div>
            </IconButton>
          )}
          {user?.clinicUser.receiveMessages && (
            <IconButton
              edge='end'
              aria-label={t('aria.chat')}
              onClick={handleOpenChat}
              disabled={isActionsDisabled}
              sx={{
                borderRadius: '8px',
                transition: 'background-color 120ms ease, color 120ms ease',
                '&:hover': {
                  backgroundColor: 'var(--app-row-hover)',
                },
                '&.Mui-disabled .MuiSvgIcon-root': {
                  color: 'var(--app-text-muted)',
                  opacity: 0.45,
                },
              }}
            >
              <Badge badgeContent={unreadMessagesCount} className={styles.chatIconBadge} color='primary'>
                <ChatIcon
                  className={styles.chatIcon}
                  sx={{
                    color: isActionsDisabled ? 'var(--app-text-muted)' : 'var(--app-chat)',
                    '& path': {
                      fill: isActionsDisabled ? 'var(--app-text-muted)' : 'var(--app-chat)',
                    },
                    transition: 'color 120ms ease',
                  }}
                />
              </Badge>
            </IconButton>
          )}
          <DotMenu hasBadState={hasBadState} onDialogOpen={onDialogOpen} />
        </Box>
      }
    >
      <ListItemText
        primary={patient.userName}
        className={styles.userName}
        primaryTypographyProps={{ sx: { textAlign: isRtl ? 'right' : 'left' } }}
        secondary={
          <Stack
            component='span'
            spacing='5px'
            direction={isRtl ? 'row-reverse' : 'row'}
            alignItems='center'
            justifyContent={isRtl ? 'flex-end' : 'flex-start'}
            sx={{ width: '100%' }}
          >
            {patient.device && browserIconSmall && (
              <Tooltip
                placement='bottom-start'
                classes={{ tooltip: styles.tooltip }}
                title={
                  <>
                    <ModalTitle title={t('dialogs.statusTitle')} />
                    <PatientStatus patient={patient} />
                  </>
                }
              >
                <Box component='span' className={styles.statusIconWrap}>
                  {browserIconSmall}
                </Box>
              </Tooltip>
            )}
            <Box
              component='span'
              sx={{
                color: waitingColor,
                fontWeight: 500,
                textAlign: isRtl ? 'right' : 'left',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {t('waitingMinutes', { count: waitingTime })}
            </Box>
            {hasBadState && (
              <Tooltip
                classes={{ tooltip: styles.tooltip }}
                title={
                  <>
                    <ModalTitle title={t('dialogs.statusTitle')} />
                    <PatientStatus patient={patient} />
                  </>
                }
                placement='right-start'
              >
                <Box component='span'>
                  <ErrorOutlineIcon sx={{ fontSize: '0.9rem' }} />
                </Box>
              </Tooltip>
            )}
          </Stack>
        }
      />
    </ListItem>
  );
}
